import { useState } from 'react'
import type { AnswerValue, WorkflowStage } from '../../types/workflow'

interface MedicineSelectionTemplateProps {
  stage: WorkflowStage
  initialValue?: string
  onSelect: (value: AnswerValue) => void
}

const OPTIONS = [
  {
    id: 'carnisema',
    name: 'Compounded Semaglutide + L-Carnitine',
    branded: 'CarniSema®',
    description: 'Clinically proven GLP-1 enhanced with L-Carnitine for metabolic support and energy.',
    image: '/img/semaglutide_plan.webp',
  },
  {
    id: 'carnitide',
    name: 'Compounded Tirzepatide + L-Carnitine',
    branded: 'CarniTide®',
    description: 'Dual-action GLP-1/GIP formula enhanced with L-Carnitine for maximum metabolic support.',
    image: '/img/tirzepatide_plan.webp',
  },
]

export default function MedicineSelectionTemplate({ initialValue, onSelect }: MedicineSelectionTemplateProps) {
  const [selected, setSelected] = useState(initialValue || '')
  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {OPTIONS.map((option) => {
          const isSelected = selected === option.id
          const nameParts = option.name.split('+')
          return (
            <button
              key={option.id}
              onClick={() => setSelected(option.id)}
              className={`relative flex flex-col gap-3 py-[18px] px-3.5 text-left rounded-2xl cursor-pointer transition-all duration-300 ease-out border glass hover:border-[var(--ww-border-hover)] hover:-translate-y-0.5 ${
                isSelected
                  ? 'border-[var(--ww-accent)] choice-selected selected-glow'
                  : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)]'
              }`}
            >

              <div className="relative shrink-0 w-full h-[120px] rounded-xl overflow-hidden bg-white/5">
                <img
                  src={option.image}
                  alt={`${option.branded} medication vial`}
                  className="w-full h-full object-contain p-2"
                />
              </div>

              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[var(--ww-accent)] m-0">{option.branded}</p>
                <h3 className="font-secondary text-[20px] text-[var(--ww-text)] m-0 leading-snug">
                  {nameParts.length > 1 ? (
                    <>{nameParts[0]}+<br />{nameParts[1]}</>
                  ) : (
                    option.name
                  )}
                </h3>
                <p className="text-[11px] text-[var(--ww-text-2)] m-0 leading-relaxed mt-0.5">{option.description}</p>
              </div>
            </button>
          )
        })}
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          disabled={!selected}
          onClick={() => onSelect({ product: selected })}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
